import { z } from 'zod';
import { birthDateSchema, optionalInput, phoneSchema } from '../api';
import { BLOOD_GROUP, GUARDIAN_RELATION, PATIENT_CATEGORY, SEX } from '../enums';
import { stripNationalId } from '../national-id';
import { ID_MISSING_REASONS, IDENTITY_DOC_TYPES } from '../countries/types';
import { withoutBidiControls } from '../text';
/** CNIC is the Pakistani national ID: 13 digits, optionally hyphenated. */
export const cnicSchema = z
    .string()
    .trim()
    .regex(/^\d{5}-?\d{7}-?\d$/, 'CNIC must be 13 digits')
    .transform((v) => v.replace(/-/g, ''));
export const nationalIdInputSchema = z
    .string()
    .trim()
    .max(24, 'The ID number is too long')
    .transform(stripNationalId)
    .refine((value) => /^[0-9A-Za-z]{4,20}$/.test(value), 'The ID number may only contain letters and digits')
    .transform((value) => value.toUpperCase());
const countryCodeSchema = z.string().trim().toUpperCase().regex(/^[A-Z]{2}$/, 'Use the two-letter country code');
export const preferredLanguageSchema = z
    .enum(['en', 'ur', 'ur-Latn', 'ar', 'es'])
    .transform((value) => (value === 'ur' ? 'ur-Latn' : value));
// Only the name and sex are mandatory; everything else is filled in when the
// desk knows it. A blank optional field is "unknown", never a validation error.
export const ADULT_AGE_YEARS = 18;
export function ageInYears(dob, approximateAgeYears) {
    if (typeof approximateAgeYears === 'number')
        return approximateAgeYears;
    if (!dob)
        return null;
    const born = new Date(`${dob}T00:00:00.000Z`);
    if (Number.isNaN(born.getTime()))
        return null;
    const now = new Date();
    let years = now.getUTCFullYear() - born.getUTCFullYear();
    const beforeBirthday = now.getUTCMonth() < born.getUTCMonth() || (now.getUTCMonth() === born.getUTCMonth() && now.getUTCDate() < born.getUTCDate());
    if (beforeBirthday)
        years -= 1;
    return years;
}
export const BIRTH_WEIGHT_GRAMS = { min: 300, max: 7000 };
export const GESTATIONAL_AGE_WEEKS = { min: 20, max: 45 };
export const GESTATIONAL_AGE_EXTRA_DAYS_MAX = 6;
export const BIRTH_DETAILS_AGE_LIMIT_DAYS = 366;
export function isInfant(dob, approximateAgeYears, now = new Date()) {
    if (dob) {
        const born = Date.parse(`${dob}T00:00:00.000Z`);
        if (Number.isNaN(born))
            return false;
        const days = (now.getTime() - born) / 86_400_000;
        return days >= 0 && days < BIRTH_DETAILS_AGE_LIMIT_DAYS;
    }
    return approximateAgeYears === 0;
}
const birthWeightGramsSchema = z.number().int().min(BIRTH_WEIGHT_GRAMS.min).max(BIRTH_WEIGHT_GRAMS.max);
const gestationalAgeWeeksSchema = z.number().int().min(GESTATIONAL_AGE_WEEKS.min).max(GESTATIONAL_AGE_WEEKS.max);
const gestationalAgeDaysSchema = z.number().int().min(0).max(GESTATIONAL_AGE_EXTRA_DAYS_MAX);
const patientFieldsSchema = z.object({
    firstName: z.preprocess(withoutBidiControls, z.string().trim().min(1).max(80)),
    lastName: optionalInput(z.preprocess(withoutBidiControls, z.string().trim().max(80))),
    middleName: optionalInput(z.preprocess(withoutBidiControls, z.string().trim().max(80))),
    secondLastName: optionalInput(z.preprocess(withoutBidiControls, z.string().trim().max(80))),
    fullNameLocal: optionalInput(z.preprocess(withoutBidiControls, z.string().trim().max(200))),
    fatherOrHusbandName: optionalInput(z.preprocess(withoutBidiControls, z.string().trim().max(160))),
    sex: z.enum(SEX).default('unknown'),
    dob: optionalInput(birthDateSchema),
    /** Used when a patient does not know their date of birth. */
    approximateAgeYears: optionalInput(z.number().int().min(0).max(130)),
    birthWeightGrams: optionalInput(birthWeightGramsSchema),
    gestationalAgeWeeks: optionalInput(gestationalAgeWeeksSchema),
    gestationalAgeDays: optionalInput(gestationalAgeDaysSchema),
    cnic: optionalInput(nationalIdInputSchema),
    idType: z.enum(IDENTITY_DOC_TYPES).optional(),
    idCountry: optionalInput(countryCodeSchema),
    idExpiry: optionalInput(z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Use a YYYY-MM-DD date')),
    idMissingReason: z.enum(ID_MISSING_REASONS).optional(),
    nationality: optionalInput(countryCodeSchema),
    occupationCode: optionalInput(z.string().trim().max(40)),
    phone: optionalInput(phoneSchema),
    altPhone: optionalInput(phoneSchema),
    email: optionalInput(z.string().trim().email()),
    bloodGroup: z.enum(BLOOD_GROUP).default('unknown'),
    category: z.enum(PATIENT_CATEGORY).default('general'),
    maritalStatus: z.enum(['single', 'married', 'widowed', 'divorced', 'unknown']).default('unknown'),
    address: optionalInput(z.string().trim().max(400)),
    city: optionalInput(z.string().trim().max(80)),
    country: optionalInput(z.string().trim().max(80)),
    preferredLanguage: preferredLanguageSchema.default('en'),
    guardianName: optionalInput(z.string().trim().max(160)),
    guardianRelation: z.enum(GUARDIAN_RELATION).default('self'),
    guardianPhone: optionalInput(phoneSchema),
    guardianCnic: optionalInput(nationalIdInputSchema),
    guardianIdType: z.enum(IDENTITY_DOC_TYPES).optional(),
    emergencyContactName: optionalInput(z.string().trim().max(160)),
    emergencyContactPhone: optionalInput(phoneSchema),
    platformPersonId: z.string().uuid().nullable().optional(),
    branchId: z.string().uuid().optional(),
    notes: optionalInput(z.string().max(2000)),
    /** Set when the desk consciously overrides a duplicate warning. */
    acknowledgeDuplicate: z.boolean().default(false),
});
export const FAMILY_RELATION = ['mother', 'father', 'parent', 'child', 'spouse', 'sibling', 'other'];
export const familyLinkSchema = z.object({
    parentPatientId: z.string().uuid(),
    relation: z.enum(FAMILY_RELATION),
});
export function familyLinkNamesGuardian(link) {
    return Boolean(link && link.relation !== 'child');
}
export const familyCoverageSchema = z.object({
    patientId: z.string().uuid(),
    coverageId: z.string().uuid(),
    relation: z.enum(['child', 'spouse', 'parent', 'other']).default('child'),
});
export const GESTATION_DAYS_NEED_WEEKS = 'Enter the completed weeks of gestation before the extra days';
function gestationDaysWithoutWeeks(value, weeksKnownElsewhere) {
    if (value.gestationalAgeDays === undefined || value.gestationalAgeDays === null)
        return false;
    if (value.gestationalAgeWeeks === null)
        return true;
    return value.gestationalAgeWeeks === undefined && !weeksKnownElsewhere;
}
export const registerPatientSchema = patientFieldsSchema.extend({ family: familyLinkSchema.optional() }).superRefine((value, ctx) => {
    if (gestationDaysWithoutWeeks(value, false)) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['gestationalAgeDays'], message: GESTATION_DAYS_NEED_WEEKS });
    }
    const age = ageInYears(value.dob, value.approximateAgeYears);
    const isMinor = age !== null && age < ADULT_AGE_YEARS;
    if (isMinor && familyLinkNamesGuardian(value.family) && !value.guardianName?.trim())
        return;
    // A minor cannot be their own guardian; an adult managing someone else's
    // visit (an ageing parent, for instance) is not covered by age at all —
    // so a named relation other than Self also pulls in the guardian fields.
    if (isMinor && value.guardianRelation === 'self') {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['guardianRelation'],
            message: 'A patient under 18 needs a guardian — pick their relation to the patient',
        });
    }
    const needsNamedGuardian = isMinor || value.guardianRelation !== 'self';
    if (!needsNamedGuardian)
        return;
    if (!value.guardianName?.trim()) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['guardianName'],
            message: isMinor ? 'A patient under 18 needs a guardian' : 'Name the guardian, or set the relation to Self',
        });
    }
    if (!value.guardianPhone?.trim()) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['guardianPhone'],
            message: isMinor ? 'A guardian phone number is needed for a patient under 18' : 'A guardian phone number is needed',
        });
    }
});
const fields = patientFieldsSchema.shape;
export const updatePatientSchema = patientFieldsSchema
    .partial()
    .omit({
    acknowledgeDuplicate: true,
    platformPersonId: true,
})
    .extend({
    lastName: fields.lastName.nullable(),
    middleName: fields.middleName.nullable(),
    secondLastName: fields.secondLastName.nullable(),
    fullNameLocal: fields.fullNameLocal.nullable(),
    fatherOrHusbandName: fields.fatherOrHusbandName.nullable(),
    dob: fields.dob.nullable(),
    approximateAgeYears: fields.approximateAgeYears.nullable(),
    birthWeightGrams: fields.birthWeightGrams.nullable(),
    gestationalAgeWeeks: fields.gestationalAgeWeeks.nullable(),
    gestationalAgeDays: fields.gestationalAgeDays.nullable(),
    occupationCode: fields.occupationCode.nullable(),
    phone: fields.phone.nullable(),
    altPhone: fields.altPhone.nullable(),
    email: fields.email.nullable(),
    address: fields.address.nullable(),
    city: fields.city.nullable(),
    guardianName: fields.guardianName.nullable(),
    guardianPhone: fields.guardianPhone.nullable(),
    emergencyContactName: fields.emergencyContactName.nullable(),
    emergencyContactPhone: fields.emergencyContactPhone.nullable(),
    notes: fields.notes.nullable(),
})
    .superRefine((value, ctx) => {
    if (gestationDaysWithoutWeeks(value, true)) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['gestationalAgeDays'], message: GESTATION_DAYS_NEED_WEEKS });
    }
});
export const associatePatientSchema = z.object({
    platformPersonId: z.string().uuid(),
    branchId: z.string().uuid().optional(),
    category: z.enum(PATIENT_CATEGORY).default('general'),
});
export const searchPatientsSchema = z.object({
    q: z.string().trim().max(120).optional(),
    mrn: z.string().trim().max(40).optional(),
    phone: z.string().trim().max(20).optional(),
    cnic: z.string().trim().max(20).optional(),
    category: z.enum(PATIENT_CATEGORY).optional(),
    branchId: z.string().uuid().optional(),
    limit: z.coerce.number().int().min(1).max(100).default(25),
    cursor: z.string().optional(),
});
export const mergePatientsSchema = z.object({
    survivingPatientId: z.string().uuid(),
    mergedPatientId: z.string().uuid(),
    reason: z.string().trim().min(10).max(500),
});
export const patientConsentSchema = z
    .object({
    type: z.enum(['general', 'procedure', 'anesthesia', 'data_sharing', 'telemedicine', 'research']),
    documentUrl: z.string().url().nullable().optional(),
    signatureRef: z.string().max(400).nullable().optional(),
    signedByName: z.string().trim().max(160),
    relation: z.string().trim().max(60).default('self'),
    notes: z.string().max(1000).optional(),
    expiresAt: z.string().datetime().nullable().optional(),
})
    .refine((value) => !value.expiresAt || Date.parse(value.expiresAt) > Date.now(), {
    message: 'A consent cannot expire in the past',
    path: ['expiresAt'],
});
export const patientDocumentSchema = z.object({
    title: z.string().trim().min(2).max(160),
    kind: z.enum(['id', 'referral', 'old_record', 'insurance', 'report', 'other']).default('other'),
    fileKey: z.string().min(3).max(500),
    mimeType: z.string().max(120),
    sizeBytes: z.number().int().min(0),
});
export const PATIENT_PHOTO_FOLDER = 'patient-photos';
export const PATIENT_PHOTO_MAX_BYTES = 2 * 1024 * 1024;
export const PATIENT_PHOTO_MAX_EDGE_PX = 600;
export const PATIENT_PHOTO_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
export const patientPhotoSchema = z.object({
    fileKey: z.string().trim().min(3).max(500),
});
export const nextMrnQuerySchema = z.object({
    branchId: z.string().uuid().optional(),
});
export const patientContactSchema = z.object({
    name: z.string().trim().min(2).max(160),
    relation: z.string().trim().min(2).max(60),
    phone: optionalInput(phoneSchema),
    cnic: optionalInput(nationalIdInputSchema),
    canConsent: z.boolean().default(true),
    isPrimary: z.boolean().default(false),
    notes: z.string().trim().max(400).optional(),
});
//# sourceMappingURL=patients.js.map